import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  Alert
} from '@mui/material';
import { NodeType } from '../../types';
import FacebookIntegration from '../FacebookIntegration';

interface FacebookPage {
  id: string;
  name: string;
}

interface FacebookStepConfigProps {
  type: NodeType;
  config: Record<string, any>;
  onChange: (config: Record<string, any>) => void;
}

const FacebookStepConfig: React.FC<FacebookStepConfigProps> = ({ type, config, onChange }) => {
  const [selectedPage, setSelectedPage] = useState<FacebookPage | null>(
    config.pageId && config.pageName ? { id: config.pageId, name: config.pageName } : null
  );

  // Handle page selection from FacebookIntegration component
  const handlePageSelect = (page: FacebookPage) => {
    setSelectedPage(page);
    
    // Update config with selected page
    const updatedConfig = {
      ...config,
      pageId: page.id,
      pageName: page.name
    };
    
    onChange(updatedConfig);
  };

  // Handle text field changes
  const handleTextChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedConfig = {
      ...config,
      [field]: event.target.value
    };
    
    onChange(updatedConfig);
  };

  // Handle date field changes
  const handleDateChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedConfig = {
      ...config,
      [field]: event.target.value
    };
    
    onChange(updatedConfig);
  };

  // Render different configuration forms based on step type
  const renderConfigForm = () => {
    switch (type) {
      case NodeType.FACEBOOK_CREATE_POST:
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Create Facebook Post
            </Typography>
            
            <TextField
              fullWidth
              label="Message"
              value={config.message || ''}
              onChange={handleTextChange('message')}
              margin="normal"
              multiline
              rows={4}
              helperText="The text content of your post"
            />
            
            <TextField
              fullWidth
              label="Link (optional)"
              value={config.link || ''}
              onChange={handleTextChange('link')}
              margin="normal"
              helperText="URL to include in the post"
            />
            
            <TextField
              fullWidth
              label="Image URL (optional)"
              value={config.imageUrl || ''}
              onChange={handleTextChange('imageUrl')}
              margin="normal"
              helperText="URL of an image to include in the post"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={!!config.scheduled}
                  onChange={(e) => {
                    const updatedConfig = {
                      ...config,
                      scheduled: e.target.checked
                    };
                    onChange(updatedConfig);
                  }}
                />
              }
              label="Schedule post"
              sx={{ mt: 2 }}
            />
            
            {config.scheduled && (
              <TextField
                fullWidth
                label="Scheduled Time"
                type="datetime-local"
                value={config.scheduledTime || ''}
                onChange={handleDateChange('scheduledTime')}
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            )}
          </Box>
        );
        
      case NodeType.FACEBOOK_GET_POSTS:
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Get Facebook Posts
            </Typography>
            
            <TextField
              fullWidth
              label="Limit"
              type="number"
              value={config.limit || 10}
              onChange={handleTextChange('limit')}
              margin="normal"
              helperText="Maximum number of posts to retrieve"
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>
        );
        
      case NodeType.FACEBOOK_UPDATE_POST:
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Update Facebook Post
            </Typography>
            
            <TextField
              fullWidth
              label="Post ID"
              value={config.postId || ''}
              onChange={handleTextChange('postId')}
              margin="normal"
              helperText="ID of the post to update"
              required
            />
            
            <TextField
              fullWidth
              label="Message"
              value={config.message || ''}
              onChange={handleTextChange('message')}
              margin="normal"
              multiline
              rows={4}
              helperText="The new text content for the post"
              required
            />
          </Box>
        );
        
      case NodeType.FACEBOOK_DELETE_POST:
        return (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Delete Facebook Post
            </Typography>
            
            <TextField
              fullWidth
              label="Post ID"
              value={config.postId || ''}
              onChange={handleTextChange('postId')}
              margin="normal"
              helperText="ID of the post to delete"
              required
            />
          </Box>
        );
        
      default:
        return (
          <Alert severity="warning">
            Unsupported Facebook step type: {type}
          </Alert>
        );
    }
  };

  return (
    <Box>
      <FacebookIntegration onPageSelect={handlePageSelect} />
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step Configuration
        </Typography>
        
        {selectedPage ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Selected Page: {selectedPage.name}
            </Alert>
            
            {renderConfigForm()}
          </Box>
        ) : (
          <Alert severity="warning">
            Please connect to Facebook and select a page first.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default FacebookStepConfig;
